import { ReactNode } from 'react';
import styled from 'styled-components';

import { H2 } from 'components/Text/H2';
import { useHideDocumentScroll } from 'hooks/useHideDocumentScroll';
import { useIsMounted } from 'hooks/useIsMounted';
import { CloseButton, Dialog, ModalBase } from 'modals/ModalBase';
import { colors, cssBreakpoints } from 'theme/theme';

const StyledDialog = styled(Dialog)<{
  $maxWidth: 'none' | number | undefined;
  $wide: boolean;
  $noPadding: boolean;
}>`
  background-image: linear-gradient(
    to left,
    ${colors.hoverBlue},
    ${colors.secondarySkyBlue}
  );
  background-size: 100% 7px;
  background-repeat: no-repeat;
  padding: ${(props) => (props.$noPadding ? undefined : '42px 20px 35px')};
  max-width: ${(props) => {
    if (typeof props.$maxWidth === 'undefined') return;

    if (props.$maxWidth === 'none') return 'none';

    return `${props.$maxWidth}px}`;
  }};

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    padding: ${(props) => (props.$noPadding ? undefined : '40px')};
    ${(props) => props.$wide && `min-width: 650px`}
  }
`;

const StyledCloseButton = styled(CloseButton)`
  color: ${colors.mediumContentGrey};
  height: 24px;
  width: 24px;

  &:hover {
    color: ${colors.brandBlue};

    &:disabled {
      &:hover {
        color: ${colors.lightContentGrey};
      }
    }
  }
`;

type Props = {
  children: ReactNode;
  className?: string;
  uncloseable?: boolean;
  image?: ReactNode;
  title?: ReactNode;
  onClose?: () => void;
  'data-qa-id'?: string;
  wide?: boolean;
  maxWidth?: 'none' | number;
  noPadding?: boolean;
};

export function Modal({
  children,
  className,
  uncloseable,
  title,
  image,
  onClose,
  wide,
  'data-qa-id': dataQaId,
  maxWidth,
  noPadding,
}: Props) {
  const isMounted = useIsMounted();

  useHideDocumentScroll({
    show: isMounted(),
  });

  return (
    <ModalBase className={className} onClose={onClose}>
      <StyledDialog
        data-qa-id={dataQaId}
        $wide={Boolean(wide)}
        $maxWidth={maxWidth}
        $noPadding={Boolean(noPadding)}
      >
        {onClose && !uncloseable && (
          <StyledCloseButton
            onClick={onClose}
            data-qa-id="close-modal-button"
            data-modal-focus="false"
          />
        )}

        {image}

        <div>
          {title && (
            <H2 styleAs="h3" pb={2}>
              {title}
            </H2>
          )}

          {children}
        </div>
      </StyledDialog>
    </ModalBase>
  );
}
